<template>
    <main>
        <!-- Hero section -->
        <section
            class="max-w-md mx-auto py-24 px-4 divide-y-2 divide-blue-gray-200 sm:max-w-3xl sm:px-6 lg:max-w-7xl lg:py-32 lg:px-8"
            aria-labelledby="faq-heading"
        >
            <h2 id="heading" class="text-3xl font-extrabold text-blue-gray-900">
                {{ $tc('messages.activities', 2) }}
            </h2>

            <AppWell>
                <AppCard>
                    <form :action="meta.links.index" method="GET" @submit.prevent="filter" class="px-2">
                        <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 md:grid-cols-12">
                            <div class="sm:col-span-4">
                                <label class="block text-sm font-medium leading-5 text-gray-700">{{
                                    $t('messages.search')
                                }}</label>
                                <div class="mt-1 relative rounded-md shadow-sm">
                                    <input
                                        v-model="form.search"
                                        :placeholder="`${$t('messages.search')}: ${$t('messages.title')}`"
                                        type="text"
                                        class="pr-10 border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm w-full"
                                    />
                                    <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                        <svg class="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                            <path
                                                fill-rule="evenodd"
                                                d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                                                clip-rule="evenodd"
                                            ></path>
                                        </svg>
                                    </div>
                                </div>
                            </div>

                            <div class="sm:col-span-2">
                                <div class="mt-1">
                                    <FormLabel :value="$tc('messages.subject', 1)" for="subject_code" />
                                    <FormSelect
                                        id="subject_code"
                                        v-model="form.subject_code"
                                        :options="options.subject_codes"
                                        class="mt-1 w-full"
                                    />
                                    <FormError :value="form.errors.subject_code" class="mt-2" />
                                </div>
                            </div>

                            <div class="sm:col-span-2">
                                <div class="mt-1">
                                    <FormLabel :value="$tc('messages.languages', 1)" for="locale" />
                                    <FormSelect
                                        id="locale"
                                        v-model="form.locale"
                                        :options="options.locales"
                                        class="mt-1 w-full"
                                    />
                                    <FormError :value="form.errors.locale" class="mt-2" />
                                </div>
                            </div>

                            <div class="sm:col-span-2">
                                <div class="mt-1">
                                    <FormLabel :value="$t('messages.type')" for="type" />
                                    <FormSelect
                                        id="type"
                                        v-model="form.type"
                                        :options="options.types"
                                        class="mt-1 w-full"
                                    />
                                    <FormError :value="form.errors.type" class="mt-2" />
                                </div>
                            </div>

                            <div class="sm:col-span-2">
                                <label class="block text-sm font-medium leading-5 text-gray-700"> &nbsp; </label>
                                <div class="flex justify-end space-x-2 mt-1">
                                    <SubmitFormButton
                                        :button-text="$t('messages.filter')"
                                        :processing="form.processing"
                                    />
                                </div>
                            </div>
                        </div>
                    </form>
                </AppCard>
            </AppWell>

            <AppWell class="mt-1">
                <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div
                        v-for="activity in activities.data"
                        :key="activity.uuid"
                        class="relative flex items-center space-x-3 rounded-lg border border-gray-300 bg-white px-6 py-5 shadow-sm focus-within:ring-2 focus-within:ring-indigo-500 focus-within:ring-offset-2 hover:border-gray-400"
                    >
                        <div class="flex-shrink-0">
                            <div
                                class="h-10 w-10 bg-indigo-200 rounded-full flex justify-center align-center items-center"
                            >
                                <ActivityIcon
                                    :type="activity.icon_type"
                                    :title="activity.type_display"
                                    classes="h-8 w-8 text-indigo-400"
                                />
                            </div>
                        </div>
                        <div class="min-w-0 flex-1">
                            <div class="flex justify-between">
                                <div>
                                    <InertiaLink
                                        class="text-base font-medium text-indigo-500"
                                        :href="activity.links.show"
                                    >
                                        {{ activity.title }}
                                    </InertiaLink>
                                </div>
                                <div>
                                    <span class="truncate text-sm text-gray-500">{{ activity.subject_name }}</span>
                                </div>
                            </div>
                            <div class="flex justify-between">
                                <div>
                                    <span class="text-sm font-medium text-gray-500">{{ activity.elements_info }}</span>
                                    <span class="text-sm font-medium text-gray-500 pl-2">({{ activity.locale }})</span>
                                </div>
                                <div>
                                    <span class="text-sm font-medium text-gray-500"> {{ activity.stage_name }} </span>:
                                    <span class="text-sm font-medium text-gray-500">{{ activity.grade_levels }}</span>
                                </div>
                            </div>
                            <div class="flex justify-between">
                                <div></div>
                                <div>
                                    <span class="text-sm font-medium text-gray-500">
                                        {{ $t('messages.shared_by_X', { author: activity.author }) }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </AppWell>

            <div class="mt-3">
                <InertiaPagination :paginator="activities" />
            </div>
        </section>
    </main>
</template>

<script>
import LandingLayout from '@/Shared/LandingLayout.vue';
import AppWell from '@/Shared/AppWell.vue';
import ActivityIcon from '@/components/ActivityIcon.vue';
import InertiaPagination from '@/Shared/InertiaPagination.vue';
import AppCard from '@/Shared/AppCard.vue';
import FormError from '@/components/Forms/FormError.vue';
import FormSelect from '@/components/Forms/FormSelect.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';

export default {
    name: 'Index',
    props: ['activities', 'meta', 'options', 'filters'],
    components: {
        SubmitFormButton,
        FormLabel,
        FormSelect,
        FormError,
        AppCard,
        InertiaPagination,
        ActivityIcon,
        AppWell,
    },
    layout: LandingLayout,
    data() {
        return {
            form: this.$inertia.form({
                search: this.filters.search,
                subject_code: this.filters.subject_code,
                type: this.filters.type,
                locale: this.filters.locale,
            }),
        };
    },
    methods: {
        filter() {
            this.form.get(this.meta.links.index);
        },
    },
};
</script>
